import useAppQuery from 'hooks/useAppQuery';
import useAppUser from 'hooks/useAppUser';
import { GetDbsAgencyResponseTO } from 'models/AgencyDbs';
import API from 'services/API';
import useFetchAgencyDbsConfig from '../useFetchAgencyDbsConfig';

export const DBS_WHITE_LABEL_SETTINGS_BASE_QUERY_KEY = 'dbsWhiteLabelSettings';
export const DBS_AGENCY_URL = '/api/public/dbs/agency';

// Origin plus the first path segment, exactly what the booking site sends about itself.
// The backend resolves the host as a custom domain first, then that segment as the agency.
export const getDbsHostnameHeader = (website: string) => {
  const withScheme = /^https?:\/\//i.test(website)
    ? website
    : `https://${website}`;

  try {
    const url = new URL(withScheme);
    const [, firstSegment = ''] = url.pathname.split('/');

    return `${url.origin}/${firstSegment}`;
  } catch {
    return withScheme;
  }
};

/**
 * The saved favicon and the legacy white-label flag are only readable through the payload
 * the booking site boots from, which is keyed by hostname rather than agency — so this
 * waits for the booking website URL and asks on the site's behalf.
 */
const useFetchDbsWhiteLabelSettings = () => {
  const { agencyUid } = useAppUser();
  const {
    agencyDbsConfig,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = useFetchAgencyDbsConfig();
  const website = agencyDbsConfig?.website;

  const { data, isInitialLoading, isError } = useAppQuery(
    [DBS_WHITE_LABEL_SETTINGS_BASE_QUERY_KEY, agencyUid],
    async () => {
      const response = await API.get<GetDbsAgencyResponseTO>(DBS_AGENCY_URL, {
        headers: { 'X-Hostfully-Hostname': getDbsHostnameHeader(website!) },
      });

      return response.data.agency.whiteLabelSettings;
    },
    { enabled: !!agencyUid && !!website },
  );

  return {
    whiteLabelSettings: data,
    isLoading: isConfigLoading || isInitialLoading,
    isError: isConfigError || isError,
  };
};

export default useFetchDbsWhiteLabelSettings;
